import { useState } from 'preact/hooks'
import { Sparkles, X, AlertTriangle } from 'lucide-preact'

interface Props {
    isOpen: boolean
    onClose: () => void
    onConfirm: () => Promise<void> | void
    /** Whether the LP has a prior live deployment whose URL will be restored. */
    hasPriorDeployment: boolean
}

export function SwitchToBuilderModal({ isOpen, onClose, onConfirm, hasPriorDeployment }: Props) {
    const [submitting, setSubmitting] = useState(false)

    if (!isOpen) return null

    const handleClose = () => {
        if (!submitting) onClose()
    }

    const handleConfirm = async () => {
        setSubmitting(true)
        try {
            await onConfirm()
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm" onClick={handleClose} />
            <div className="relative w-full max-w-md rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-xl p-5 space-y-4">
                <h3 className="text-lg font-semibold text-slate-900 dark:text-white flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-primary-600" /> Switch to Builder mode
                </h3>
                <div className="flex items-start gap-2 rounded p-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                    <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        {hasPriorDeployment
                            ? 'Your custom URL will be replaced with the latest deployed builder URL.'
                            : 'Your custom URL will be cleared. Generate and deploy a builder page to publish a new URL.'}
                    </p>
                </div>
                <p className="text-sm text-slate-600 dark:text-slate-300">
                    Continue to switch this landing page to the AI builder?
                </p>
                <div className="flex items-center justify-end gap-2 pt-1">
                    <button
                        type="button"
                        onClick={handleClose}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50"
                    >
                        <X className="w-4 h-4" /> Cancel
                    </button>
                    <button
                        type="button"
                        onClick={handleConfirm}
                        disabled={submitting}
                        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50"
                    >
                        {submitting ? 'Switching…' : 'Switch to Builder'}
                    </button>
                </div>
            </div>
        </div>
    )
}
